import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';

const faqItems = [
  { question: 'Posso usare Itaobuy Spreadsheet dal mio telefono?', answer: 'Sì, la piattaforma è completamente responsive e funziona perfettamente su smartphone e tablet, anche se non esiste ancora un\'app nativa.' },
  { question: 'Quanto spesso vengono aggiunti nuovi prodotti?', answer: 'Il database viene aggiornato quotidianamente con nuovi prodotti e offerte. In media vengono aggiunti oltre 100 nuovi prodotti ogni settimana.' },
];

export default function ComeUsarePage() {
  return (
    <ArticleLayout
      title="Come Usare Itaobuy Spreadsheet: La Guida Visiva Passo Dopo Passo"
      description="Guida visiva completa con spiegazioni dettagliate per imparare a usare Itaobuy Spreadsheet in modo efficace. Dalla navigazione base alle funzionalità avanzate."
      tag="How-To"
      slug="come-usare-itaobuy-spreadsheet"
      faqItems={faqItems}
    >
      <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-6 mb-12">
        <p className="text-sm font-semibold text-accent-800 mb-2 font-label">TL;DR &mdash; In 5 Minuti</p>
        <p className="text-sm text-accent-700 leading-relaxed">
          <strong>Itaobuy Spreadsheet</strong> è più semplice di quanto pensi. Con questa guida visiva imparerai a muoverti
          tra le categorie, confrontare prodotti, leggere recensioni e completare acquisti in tutta sicurezza.
        </p>
      </div>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">La Dashboard Principale</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Quando apri <strong>Itaobuy Spreadsheet</strong>, la prima cosa che vedi è la dashboard principale. È organizzata
          in modo intuitivo con un menu di navigazione in alto e le categorie di prodotti ben visibili.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 my-8">
          <div className="bg-background-100 rounded-xl p-5">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
              <i className="ri-compass-line text-primary-600 text-lg"></i>
            </div>
            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">Navigazione Principale</h3>
            <p className="text-sm text-foreground-600">Menu con Home, Guida Acquisti, Categorie, Sicurezza e Blog. Tutto a portata di click.</p>
          </div>
          <div className="bg-background-100 rounded-xl p-5">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
              <i className="ri-grid-line text-primary-600 text-lg"></i>
            </div>
            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">Griglia Categorie</h3>
            <p className="text-sm text-foreground-600">14 categorie visualizzate come card con immagini. Clicca su una categoria per esplorarla.</p>
          </div>
        </div>
      </section>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Esplorare una Categoria</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Cliccando su una categoria, accedi all&apos;elenco dei prodotti in formato spreadsheet. Ogni riga rappresenta
          un prodotto con: nome, prezzo, valutazione in stelle e numero di recensioni.
        </p>
        <div className="bg-background-100 rounded-xl p-5 mb-6">
          <h3 className="font-heading text-base font-semibold text-foreground-900 mb-3">Cosa Guardare in un Prodotto</h3>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 font-medium">Prezzo competitivo</span>
            <span className="text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">4+ stelle</span>
            <span className="text-xs px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 font-medium">50+ recensioni</span>
            <span className="text-xs px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 font-medium">Foto reali</span>
            <span className="text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">Descrizione completa</span>
          </div>
        </div>
      </section>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Confrontare i Prodotti</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Una delle funzionalità più potenti di <strong>Itaobuy Spreadsheet</strong> è il confronto prezzi integrato.
          Puoi vedere fino a 5 prodotti affiancati per confrontare caratteristiche, prezzi e valutazioni.
        </p>
        <ol className="space-y-3 list-decimal list-inside text-foreground-700 marker:text-primary-600 marker:font-semibold">
          <li className="pl-2">Apri la categoria che ti interessa</li>
          <li className="pl-2">Seleziona i prodotti da confrontare (fino a 5)</li>
          <li className="pl-2">Visualizza la tabella comparativa con prezzi, valutazioni e recensioni</li>
          <li className="pl-2">Scegli il prodotto con il miglior rapporto qualità-prezzo</li>
        </ol>
      </section>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Completare un Acquisto</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Una volta scelto il prodotto, il processo di acquisto è lineare e sicuro. Ecco i passaggi:
        </p>
        <div className="space-y-4">
          <div className="flex gap-3">
            <span className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-700 flex-shrink-0">1</span>
            <p className="text-sm text-foreground-700"><strong>Seleziona taglia/colore</strong> — Scegli le varianti disponibili per il prodotto.</p>
          </div>
          <div className="flex gap-3">
            <span className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-700 flex-shrink-0">2</span>
            <p className="text-sm text-foreground-700"><strong>Verifica il prezzo totale</strong> — Controlla che il prezzo includa eventuali spese di spedizione.</p>
          </div>
          <div className="flex gap-3">
            <span className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-700 flex-shrink-0">3</span>
            <p className="text-sm text-foreground-700"><strong>Scegli il metodo di pagamento</strong> — Preferisci opzioni con protezione acquirente.</p>
          </div>
          <div className="flex gap-3">
            <span className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-700 flex-shrink-0">4</span>
            <p className="text-sm text-foreground-700"><strong>Conferma e attendi la spedizione</strong> — Riceverai aggiornamenti sullo stato dell&apos;ordine.</p>
          </div>
        </div>
      </section>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Consigli Extra per un&apos;Esperienza Ottimale</h2>
        <ul className="space-y-3">
          <li className="flex gap-3"><i className="ri-lightbulb-line text-accent-500 mt-0.5 flex-shrink-0"></i><p className="text-sm text-foreground-700"><strong>Salva i tuoi prodotti preferiti</strong> — Tieni traccia dei prodotti che ti interessano per monitorare i prezzi.</p></li>
          <li className="flex gap-3"><i className="ri-lightbulb-line text-accent-500 mt-0.5 flex-shrink-0"></i><p className="text-sm text-foreground-700"><strong>Leggi sempre le recensioni recenti</strong> — Le recensioni più vecchie potrebbero non riflettere la qualità attuale.</p></li>
          <li className="flex gap-3"><i className="ri-lightbulb-line text-accent-500 mt-0.5 flex-shrink-0"></i><p className="text-sm text-foreground-700"><strong>Iscriviti agli aggiornamenti</strong> — Ricevi notifiche sulle nuove offerte nella <Link to="/articoli/itaobuy-spreadsheet-categorie" className="text-primary-600 hover:text-primary-700">tua categoria preferita</Link>.</p></li>
        </ul>
      </section>

      <section className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Conclusione</h2>
        <p className="text-foreground-700 leading-relaxed">
          Ora conosci tutti i segreti per usare <strong>Itaobuy Spreadsheet</strong> al meglio. Consulta anche la
          <Link to="/articoli/itaobuy-spreadsheet-strategie-avanzate" className="text-primary-600 hover:text-primary-700"> guida alle strategie avanzate</Link> e
          torna alla <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">home</Link> per mettere in pratica ciò che hai imparato.
        </p>
      </section>
    </ArticleLayout>
  );
}